// ============================================================
// Email ingest — PURE parsing logic (runtime-agnostic, browser-safe)
// Ported from the validated reference berserkr/scripts/email_parser_ref.py.
// Strict-safe: written for tsconfig strict + noUncheckedIndexedAccess.
// ============================================================

import {
  Addr, AttachmentRef, EmailMessage, ExportRoute, HeaderGrammar,
  ParsedEmailThread, BUILD_STAMP, SCHEMA_VERSION,
} from "./types";

const RE_FROM = /^\s*From:\s*(.*)$/;
const RE_DATESNT = /^\s*(Date|Sent):\s*(.*)$/;
const RE_TO = /^\s*To:\s*(.*)$/;
const RE_CC = /^\s*Cc:\s*(.*)$/;
const RE_SUBJ = /^\s*Subject:\s*(.*)$/;
const RE_EMAIL = /[\w.\-+']+@[\w.\-]+\.\w+/;
const RE_PRINT_FURNITURE = /^\d{1,2}\/\d{1,2}\/\d{2,4},\s*\d{1,2}:\d{2}\s*[AP]M\b/;
const RE_PAGECOUNT = /^\d+\/\d+$/;
const RE_SIG_NOISE = /^(Sent from my (iPhone|iPad|mobile device))\s*$/i;

export function parseAddr(s: string): Addr {
  s = s.trim().replace(/^"|"$/g, "").trim();
  const m = RE_EMAIL.exec(s);
  const email = m ? m[0] : null;
  let name = s.replace(/<[^>]*>/g, "");
  if (email) name = name.split(email).join("");
  name = name.replace(/[<>]/g, "").replace(/^[\s"']+|[\s"']+$/g, "").trim();
  return { display_name: name || null, address: email };
}

export function parseRecipients(s: string): Addr[] {
  let parts = s.split(";");
  if (parts.length === 1) parts = s.split(/,(?=[^@]*@)/);
  return parts.map((p) => p.trim()).filter(Boolean).map(parseAddr);
}

export function stripFurniture(lines: string[], docTitle: string | null): string[] {
  const titleNorm = (docTitle || "").trim().toLowerCase();
  const out: string[] = [];
  for (const raw of lines) {
    const s = raw.replace(/\s+$/, "");
    const t = s.trim();
    if (!t) continue;
    if (RE_PRINT_FURNITURE.test(t)) continue;
    if (titleNorm && t.toLowerCase() === titleNorm) continue;
    if (RE_PAGECOUNT.test(t)) continue;
    if (RE_SIG_NOISE.test(t)) continue;
    out.push(s);
  }
  return out;
}

interface HeaderFields {
  from: Addr | null;
  date_raw: string | null;
  date_kind: "Date" | "Sent" | null;
  to: Addr[];
  cc: Addr[];
  subject: string | null;
  start: number;
  header_end: number;
  grammar: HeaderGrammar;
}
type HeaderCore = Omit<HeaderFields, "start" | "header_end" | "grammar">;

function consumeHeader(lines: string[], i: number): { fields: HeaderCore; consumed: number } {
  const buf: Record<string, string> = {};
  let cur: string | null = null;
  let j = i;
  const n = lines.length;
  while (j < n) {
    const ln = lines[j];
    if (ln === undefined) break;
    const mFrom = RE_FROM.exec(ln);
    const mDs = RE_DATESNT.exec(ln);
    const mTo = RE_TO.exec(ln);
    const mCc = RE_CC.exec(ln);
    const mSubj = RE_SUBJ.exec(ln);
    if (j > i && mFrom) break;
    if (mFrom) { cur = "from"; buf.from = mFrom[1] ?? ""; }
    else if (mDs) { cur = "date"; buf.date = mDs[2] ?? ""; buf.date_kind = mDs[1] ?? ""; }
    else if (mTo) { cur = "to"; buf.to = mTo[1] ?? ""; }
    else if (mCc) { cur = "cc"; buf.cc = mCc[1] ?? ""; }
    else if (mSubj) { cur = "subject"; buf.subject = mSubj[1] ?? ""; }
    else if ((cur === "to" || cur === "cc") && RE_EMAIL.test(ln)) { buf[cur] = (buf[cur] ?? "") + " " + ln.trim(); }
    else break;
    j++;
  }
  const dk = buf.date_kind;
  const fields: HeaderCore = {
    from: buf.from !== undefined ? parseAddr(buf.from) : null,
    date_raw: buf.date !== undefined ? buf.date.trim() : null,
    date_kind: dk === "Date" || dk === "Sent" ? dk : null,
    to: buf.to !== undefined ? parseRecipients(buf.to) : [],
    cc: buf.cc !== undefined ? parseRecipients(buf.cc) : [],
    subject: buf.subject !== undefined ? buf.subject.trim() : null,
  };
  return { fields, consumed: Math.max(1, j - i) };
}

function grammarOf(date_kind: string | null): HeaderGrammar {
  if (date_kind === "Sent") return "outlook";
  return "apple";
}

export function findHeaderBlocks(lines: string[]): HeaderFields[] {
  const blocks: HeaderFields[] = [];
  let i = 0;
  const n = lines.length;
  while (i < n) {
    const li = lines[i];
    if (li !== undefined && RE_FROM.test(li)) {
      const window = lines.slice(i, i + 8);
      const hasDateSent = window.slice(1, 6).some((w) => RE_DATESNT.test(w));
      const hasSubj = window.slice(1, 8).some((w) => RE_SUBJ.test(w));
      if (hasDateSent && hasSubj) {
        const { fields, consumed } = consumeHeader(lines, i);
        blocks.push({ ...fields, start: i, header_end: i + consumed, grammar: grammarOf(fields.date_kind) });
        i += consumed;
        continue;
      }
    }
    i++;
  }
  return blocks;
}

const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};
const pad = (x: number) => String(x).padStart(2, "0");

export function parseTs(raw: string | null): { iso: string | null; tz: string | null } {
  if (!raw) return { iso: null, tz: null };
  let s = raw.replace(/ |\xa0/g, " ").replace(/\s+/g, " ").trim().replace(/,+$/, "");
  let tz: string | null = null;
  const mtz = /\b([A-Z]{2,4})\s*$/.exec(s);
  if (mtz && mtz[1] && mtz[1] !== "AM" && mtz[1] !== "PM") { tz = mtz[1]; s = s.slice(0, mtz.index).trim(); }
  const m = new RegExp(
    "(?:[A-Za-z]+,\\s*)?([A-Za-z]{3,9})\\s+(\\d{1,2}),\\s*(\\d{4})" +
    "(?:\\s+at)?\\s+(\\d{1,2}):(\\d{2})(?::(\\d{2}))?\\s*([AP]M)", "i"
  ).exec(s);
  if (m && m[1] && m[2] && m[3] && m[4] && m[5] && m[7]) {
    const mon = MONTHS[m[1].slice(0, 3).toLowerCase()];
    if (mon) {
      let hh = parseInt(m[4], 10);
      const mm = parseInt(m[5], 10);
      const ss = m[6] ? parseInt(m[6], 10) : 0;
      const ap = m[7].toUpperCase();
      if (ap === "PM" && hh !== 12) hh += 12;
      if (ap === "AM" && hh === 12) hh = 0;
      const y = parseInt(m[3], 10), d = parseInt(m[2], 10);
      return { iso: `${y}-${pad(mon)}-${pad(d)}T${pad(hh)}:${pad(mm)}:${pad(ss)}`, tz };
    }
  }
  const md = new RegExp("(?:[A-Za-z]+,\\s*)?([A-Za-z]{3,9})\\s+(\\d{1,2}),\\s*(\\d{4})", "i").exec(s);
  if (md && md[1] && md[2] && md[3]) {
    const mon = MONTHS[md[1].slice(0, 3).toLowerCase()];
    if (mon) return { iso: `${parseInt(md[3], 10)}-${pad(mon)}-${pad(parseInt(md[2], 10))}T00:00:00`, tz };
  }
  return { iso: null, tz };
}

function djb2(str: string): string {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  return h.toString(16);
}
function canonicalId(from: Addr | null, iso: string | null, dateRaw: string | null, subject: string | null): string {
  const who = (from?.address || from?.display_name || "?").toLowerCase();
  const when = iso || dateRaw || "?";
  const subj = (subject || "").toLowerCase().replace(/^(re:|fw:|fwd:)\s*/g, "").trim();
  return djb2(`${who}|${when}|${subj}`);
}

export interface ParseInput {
  source_file: string;
  doc_title: string | null;
  export_route: ExportRoute;
  text_lines: string[];
  ocr_header_lines?: string[];
}

export function parseThread(input: ParseInput): ParsedEmailThread {
  const warnings: string[] = [];
  const lines = stripFurniture(input.text_lines, input.doc_title);
  const blocks = findHeaderBlocks(lines);

  const raw: EmailMessage[] = blocks.map((b, idx) => {
    const bodyStart = b.header_end;
    const next = blocks[idx + 1];
    const bodyEnd = next ? next.start : lines.length;
    let body = lines.slice(bodyStart, bodyEnd).join("\n").trim();
    body = body.replace(/\n?\s*Begin forwarded message:\s*$/i, "").trim();
    const { iso, tz } = parseTs(b.date_raw);
    return mkMessage(b.from, b.to, b.cc, b.subject, b.date_raw, b.date_kind, iso, tz, "text", b.grammar, body);
  });

  const first = blocks[0];
  const firstStart = first ? first.start : lines.length;
  let pre = lines.slice(0, firstStart).join("\n").trim();
  pre = pre.replace(/\n?\s*Begin forwarded message:\s*$/i, "").trim();
  if (pre) {
    const ocrLines = input.ocr_header_lines;
    if (input.export_route === "ios_mail_print" && ocrLines && ocrLines.length) {
      const ob = findHeaderBlocks(ocrLines);
      const h = ob[0];
      if (h) {
        const { iso, tz } = parseTs(h.date_raw);
        raw.unshift(mkMessage(h.from, h.to, h.cc, h.subject, h.date_raw, h.date_kind, iso, tz, "ocr", "ocr_band", pre));
      } else {
        warnings.push("iOS outer header present but OCR yielded no parseable header");
        raw.unshift(mkCover(pre, input.doc_title));
      }
    } else {
      raw.unshift(mkCover(pre, input.doc_title));
    }
  }

  const seen = new Map<string, EmailMessage>();
  for (const m of raw) {
    const cid = canonicalId(m.from_address, m.date, m.date_raw, m.subject);
    m.canonical_message_id = cid;
    const prev = seen.get(cid);
    if (!prev) {
      m.in_quoted_history = false;
      seen.set(cid, m);
    } else {
      if (m.body.length > prev.body.length) {
        prev.in_quoted_history = true;
        m.in_quoted_history = false;
        seen.set(cid, m);
      } else {
        m.in_quoted_history = true;
      }
      const canon = seen.get(cid);
      if (canon) canon.requote_count += 1;
    }
  }

  raw.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  raw.forEach((m, k) => (m.message_index = k));

  const canonical = raw.filter((m) => !m.in_quoted_history);
  const dated = (canonical.map((m) => m.date).filter(Boolean) as string[]).sort();
  const subjRaw = canonical.find((m) => m.subject)?.subject ?? input.doc_title ?? null;
  const exMatch = /^([A-Z]-?\d+)/.exec(input.source_file);

  return {
    source_file: input.source_file,
    exhibit_id: exMatch?.[1] ?? null,
    export_route: input.export_route,
    build_stamp: BUILD_STAMP,
    schema_version: SCHEMA_VERSION,
    thread_subject: subjRaw ? subjRaw.replace(/^(re:|fw:|fwd:)\s*/i, "").trim() : null,
    thread_first_at: dated[0] ?? null,
    thread_last_at: dated.length ? dated[dated.length - 1] ?? null : null,
    message_count: canonical.length,
    total_occurrences: raw.length,
    messages: raw,
    warnings,
  };
}

function mkMessage(
  from: Addr | null, to: Addr[], cc: Addr[], subject: string | null,
  dateRaw: string | null, dateKind: "Date" | "Sent" | null, iso: string | null,
  tz: string | null, dateSource: "text" | "ocr", grammar: HeaderGrammar, body: string
): EmailMessage {
  return {
    body, sender_name: from?.display_name || from?.address || null,
    sender_party: null,
    message_index: 0, source_kind: "email",
    from_address: from, to_addresses: to, cc_addresses: cc, subject,
    date: iso, date_raw: dateRaw, date_tz: tz, date_kind: dateKind, date_source: dateSource,
    in_quoted_history: false, canonical_message_id: "", requote_count: 0,
    header_grammar: grammar, attachments: extractAttachmentRefs(body), pii_flags: [],
  };
}

function mkCover(body: string, title: string | null): EmailMessage {
  return {
    body, sender_name: null, sender_party: null, message_index: 0, source_kind: "email",
    from_address: null, to_addresses: [], cc_addresses: [], subject: title,
    date: null, date_raw: null, date_tz: null, date_kind: null, date_source: null,
    in_quoted_history: false, canonical_message_id: "", requote_count: 0,
    header_grammar: "cover_note", attachments: extractAttachmentRefs(body), pii_flags: [],
  };
}

const RE_FILE = /\b([\w.\- ]+?\.(pdf|docx?|xlsx?|pptx?|csv|txt|zip))\b/gi;
const SIG_LOGO = /^image\d+\.(png|jpe?g|gif)$/i;
export function extractAttachmentRefs(body: string): AttachmentRef[] {
  const out: AttachmentRef[] = [];
  const seen = new Set<string>();
  let m: RegExpExecArray | null;
  RE_FILE.lastIndex = 0;
  while ((m = RE_FILE.exec(body))) {
    const fn = (m[1] ?? "").trim();
    if (!fn || SIG_LOGO.test(fn) || seen.has(fn.toLowerCase())) continue;
    seen.add(fn.toLowerCase());
    out.push({ filename: fn, ref_only: true, mime_type: null, size_bytes: null, content_hash: null, storage_path: null });
  }
  return out;
}
